import React, { useEffect, useState } from "react";
import api, { formatApiError, fileDownloadUrl } from "@/lib/api";
import { useT } from "@/i18n";
import UserAvatar from "@/components/UserAvatar";
import UploadButton from "@/components/UploadButton";
import GridsPanel from "@/components/GridsPanel";
import ResidentDocuments from "@/components/ResidentDocuments";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ChevronUp, ArrowLeft, Plus, X, Trash2, FileText, CheckCircle2, Circle, BedDouble } from "lucide-react";
import { toast } from "sonner";

const csv = (arr) => (arr || []).join(", ");
const toArr = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-2.5 border-t border-border/60 first:border-t-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right break-words">{value || "—"}</span>
    </div>
  );
}
function Fld({ label, children, className = "" }) {
  return <div className={`space-y-1.5 ${className}`}><Label className="text-xs">{label}</Label>{children}</div>;
}

function Section({ id, title, children, ctx, editable = true }) {
  const { isManager, editing, open, setEditing, setOpen, cancel, save, saving, t } = ctx;
  return (
    <div className="rounded-xl border border-border bg-card" data-testid={`rsection-${id}`}>
      <div className="flex items-center justify-between px-5 py-4">
        <h2 className="font-display text-lg font-bold text-primary">{title}</h2>
        <div className="flex items-center gap-2">
          {isManager && editable && (editing === id ? (
            <>
              <Button size="sm" variant="ghost" onClick={cancel}>{t("cancel")}</Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="save-rsection">{t("save")}</Button>
            </>
          ) : (
            <button className="text-sm text-primary font-medium hover:underline" onClick={() => { setEditing(id); setOpen((o) => ({ ...o, [id]: true })); }} data-testid={`redit-${id}`}>{t("edit")}</button>
          ))}
          <button onClick={() => setOpen((o) => ({ ...o, [id]: !o[id] }))} className="w-7 h-7 rounded-full hover:bg-accent flex items-center justify-center">
            <ChevronUp className={`w-4 h-4 transition-transform ${open[id] ? "" : "rotate-180"}`} />
          </button>
        </div>
      </div>
      {open[id] && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

export default function ResidentDetail({ resident, isManager, onSaved, onDelete, onBack, onAssignTask, taskRefreshKey }) {
  const { t } = useT();
  const [form, setForm] = useState(resident);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState({ profile: true, medical: true, contacts: true, care: true });
  const [tasks, setTasks] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(resident); setEditing(null); }, [resident]);
  useEffect(() => { if (resident?.id) api.get(`/residents/${resident.id}/tasks`).then((r) => setTasks(r.data)).catch(() => {}); }, [resident, taskRefreshKey]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await api.put(`/residents/${resident.id}`, form); toast.success(t("save")); setEditing(null); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const cancel = () => { setForm(resident); setEditing(null); };
  const sectionCtx = { isManager, editing, open, setEditing, setOpen, cancel, save, saving, t };

  const r = form;
  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 lg:p-8 space-y-4">
      <div className="flex items-center gap-3">
        <button className="md:hidden" onClick={onBack} data-testid="rdetail-back"><ArrowLeft className="w-5 h-5" /></button>
        <UserAvatar name={r.full_name} src={r.photo} size={64} />
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl font-bold truncate">{r.full_name}</h1>
          <p className="text-sm text-muted-foreground flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" />{t("roomNumber")} {r.room_number || "—"}{r.user_code ? <span className="text-primary font-medium"> · {r.user_code}</span> : ""}</p>
        </div>
        {isManager && <Button variant="ghost" size="icon" onClick={() => onDelete(resident.id)} data-testid="delete-resident"><Trash2 className="w-4 h-4 text-destructive" /></Button>}
      </div>

      {/* Profile */}
      <Section ctx={sectionCtx} id="profile" title={t("profile")}>
        {editing === "profile" ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <UserAvatar name={`${r.first_name || ""} ${r.last_name || ""}`.trim() || r.full_name} src={r.photo} size={48} />
              <UploadButton label={t("profilePicture")} accept="image/*" category="resident" testId="upload-resident-photo" onUploaded={(d) => set("photo", `file:${d.id}`)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Fld label={t("firstName")}><Input value={r.first_name || ""} onChange={(e) => set("first_name", e.target.value)} data-testid="resident-first-name" /></Fld>
              <Fld label={t("lastName")}><Input value={r.last_name || ""} onChange={(e) => set("last_name", e.target.value)} data-testid="resident-last-name" /></Fld>
              <Fld label={t("userCode")}><Input value={r.user_code || ""} onChange={(e) => set("user_code", e.target.value)} data-testid="resident-user-code" /></Fld>
              <Fld label={t("roomNumber")}><Input value={r.room_number || ""} onChange={(e) => set("room_number", e.target.value)} data-testid="resident-room" /></Fld>
              <Fld label={t("dateOfBirth")}><Input type="date" value={r.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} /></Fld>
              <Fld label={t("admissionDate")}><Input type="date" value={r.admission_date || ""} onChange={(e) => set("admission_date", e.target.value)} /></Fld>
              <Fld label={t("treatingTeam")}><Input value={r.treating_team || ""} onChange={(e) => set("treating_team", e.target.value)} /></Fld>
              <Fld label={t("physician")}><Input value={r.physician_name || ""} onChange={(e) => set("physician_name", e.target.value)} /></Fld>
              <Fld label={t("physicianPhone")}><Input value={r.physician_phone || ""} onChange={(e) => set("physician_phone", e.target.value)} /></Fld>
              <Fld label={t("phone")}><Input value={csv(r.phones)} onChange={(e) => set("phones", toArr(e.target.value))} /></Fld>
            </div>
          </div>
        ) : (
          <>
            <Row label={t("userCode")} value={r.user_code} />
            <Row label={t("dateOfBirth")} value={r.date_of_birth} />
            <Row label={t("admissionDate")} value={r.admission_date} />
            <Row label={t("treatingTeam")} value={r.treating_team} />
            <Row label={t("physician")} value={r.physician_name} />
            <Row label={t("physicianPhone")} value={r.physician_phone} />
            <Row label={t("phone")} value={csv(r.phones)} />
          </>
        )}
      </Section>

      {/* Medical */}
      <Section ctx={sectionCtx} id="medical" title="Medical">
        {editing === "medical" ? (
          <div className="space-y-3">
            <Fld label={`${t("medicalConditions")} (${t("separateComma")})`}><Textarea rows={2} value={csv(r.medical_conditions)} onChange={(e) => set("medical_conditions", toArr(e.target.value))} data-testid="resident-conditions" /></Fld>
            <Fld label={`${t("allergies")} (${t("separateComma")})`}><Input value={csv(r.allergies)} onChange={(e) => set("allergies", toArr(e.target.value))} /></Fld>
            <Fld label={`${t("dietaryRestrictions")} (${t("separateComma")})`}><Input value={csv(r.dietary_restrictions)} onChange={(e) => set("dietary_restrictions", toArr(e.target.value))} /></Fld>
            <Fld label={t("procedures")}><Textarea rows={2} value={r.procedures || ""} onChange={(e) => set("procedures", e.target.value)} /></Fld>
            <Fld label={t("careInstructions")}><Textarea rows={2} value={r.care_instructions || ""} onChange={(e) => set("care_instructions", e.target.value)} /></Fld>
            <div className="flex items-center gap-3 flex-wrap">
              <UploadButton label={t("uploadDocument")} accept="application/pdf,image/*,.doc,.docx" category="resident-doc" testId="upload-resident-doc" onUploaded={(d) => set("documents", [...(r.documents || []), { file_id: d.id, name: d.original_filename }])} />
              {(r.documents || []).map((d, i) => <a key={i} href={fileDownloadUrl(d.file_id)} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded bg-muted flex items-center gap-1"><FileText className="w-3 h-3" />{d.name}</a>)}
            </div>
          </div>
        ) : (
          <>
            <Row label={t("medicalConditions")} value={csv(r.medical_conditions)} />
            <Row label={t("allergies")} value={<span className={r.allergies?.length ? "text-destructive" : ""}>{csv(r.allergies) || "—"}</span>} />
            <Row label={t("dietaryRestrictions")} value={csv(r.dietary_restrictions)} />
            <Row label={t("procedures")} value={r.procedures} />
            <Row label={t("careInstructions")} value={r.care_instructions} />
            {(r.documents || []).length > 0 && <div className="pt-2 flex flex-wrap gap-1">{r.documents.map((d, i) => <a key={i} href={fileDownloadUrl(d.file_id)} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded bg-muted flex items-center gap-1"><FileText className="w-3 h-3" />{d.name}</a>)}</div>}
          </>
        )}
      </Section>

      {/* Family contacts */}
      <Section ctx={sectionCtx} id="contacts" title={t("familyContacts")}>
        {editing === "contacts" ? (
          <div className="space-y-3">
            {(r.family_contacts || []).map((c, i) => {
              const upd = (k, v) => { const cs = [...r.family_contacts]; cs[i] = { ...cs[i], [k]: v }; set("family_contacts", cs); };
              return (
                <div key={i} className="rounded-lg border border-border p-3 space-y-3" data-testid="family-contact-row">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Fld label={t("firstName")}><Input value={c.first_name ?? c.name ?? ""} onChange={(e) => upd("first_name", e.target.value)} data-testid={`family-firstname-${i}`} /></Fld>
                    <Fld label={t("lastName")}><Input value={c.last_name || ""} onChange={(e) => upd("last_name", e.target.value)} data-testid={`family-lastname-${i}`} /></Fld>
                    <Fld label={t("relationship")}><Input value={c.relationship || ""} onChange={(e) => upd("relationship", e.target.value)} /></Fld>
                    <Fld label={t("phone")}><Input value={c.phone || ""} onChange={(e) => upd("phone", e.target.value)} /></Fld>
                    <Fld label={t("email")} className="sm:col-span-2"><Input type="email" value={c.email || ""} onChange={(e) => upd("email", e.target.value)} data-testid={`family-email-${i}`} /></Fld>
                  </div>
                  <div className="flex justify-end">
                    <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => set("family_contacts", r.family_contacts.filter((_, j) => j !== i))}><X className="w-4 h-4 mr-1" />{t("remove")}</Button>
                  </div>
                </div>
              );
            })}
            <Button variant="outline" size="sm" onClick={() => set("family_contacts", [...(r.family_contacts || []), { first_name: "", last_name: "", relationship: "", phone: "", email: "" }])}><Plus className="w-4 h-4 mr-1" />{t("add")}</Button>
          </div>
        ) : (
          (r.family_contacts || []).length ? r.family_contacts.map((c, i) => {
            const nm = [c.first_name, c.last_name].filter(Boolean).join(" ") || c.name || "—";
            return <Row key={i} label={`${nm}${c.relationship ? ` (${c.relationship})` : ""}`} value={[c.phone, c.email].filter(Boolean).join(" · ")} />;
          }) : <p className="text-sm text-muted-foreground py-2">—</p>
        )}
      </Section>

      {/* Care history */}
      <Section ctx={sectionCtx} id="care" title={t("careHistory")} editable={false}>
        {isManager && <div className="flex justify-end mb-2"><Button size="sm" onClick={() => onAssignTask(resident)} data-testid="assign-rtask-button"><Plus className="w-4 h-4 mr-1" />{t("assignTask")}</Button></div>}
        <div className="space-y-1.5">
          {tasks.map((tk) => (
            <div key={tk.id} className="flex items-center gap-2 p-2 rounded-lg border border-border text-sm">
              {tk.status === "completed" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-muted-foreground" />}
              <span className="flex-1 truncate">{tk.title}</span>
              <Badge variant="outline" className="text-[10px]">{t(tk.category)}</Badge>
              <span className="text-xs text-muted-foreground">{tk.date} {tk.time}</span>
            </div>
          ))}
          {tasks.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">{t("noData")}</p>}
        </div>
      </Section>

      {/* Classified documents + AI reading */}
      <ResidentDocuments residentId={resident.id} isManager={isManager} />

      {/* Grilles de cotation — at the bottom, after Care history */}
      <GridsPanel residentId={resident.id} residentName={resident.full_name} />
    </div>
  );
}
